import { toCamelCase } from "../hooks/toCameCase.tsx";
import { calcularTiempoTrabajado } from "../interfaces/formik/IReporteRelojChecador.ts";
import { getCurrencyFormat } from "../utils/index.ts";
import dayjs from "dayjs";
import "dayjs/locale/es";
import { Incentive, IncentiveResponse } from "./Incentive.ts";
import { Deduction, DeductionResponse } from "./Deduction.ts";
import moment from "moment";
import "moment/locale/es-mx";
import { Incident } from "./Incident.ts";
import { IAuthorizedHours } from "./AuthorizedHours.ts";
import { FindChecks } from "./Check.ts";
import { v4 as uuid } from "uuid";
import { ScheduleAfterIncident } from "./ScheduleAfterIncident.ts";
import { PaymentDetail } from "./PaymentDetail.ts";
import { FreeDayResponse } from "./FreeDay.ts";
import { getExactDateFromJSON } from "../utils/dateUtils.ts";
import {
  getDaysToPay,
  getDayPerIncentive,
  getDaysBetweenDates,
  countDaysInRange,
  freeDaysTest,
  scheduleAfterIncidents,
  calculatePayment,
  parseCelebrityDayMultiplier,
} from "../services/payroll/calculation.ts";
import { withFreeDayWorkedPremium } from "../services/payroll/payPremiums.ts";

export interface CelebrityDay {
  id: number;
  date: string;
  title: string;
  bonusType: string;
  createdAt: string;
  updatedAt: string;
}

export const getCurrentFortnightDayjs = () => {
  const today = dayjs();

  const firstFortnight = {
    start: today.startOf("month").startOf("day"),
    end: dayjs().date(15).endOf("day"),
  };

  const secondFortnight = {
    start: dayjs().date(16).startOf("day"),
    end: today.endOf("month").endOf("day"),
  };

  return today.date() <= 15 ? firstFortnight : secondFortnight;
};

export interface IPayroll {
  schedule_id: number;
  schedule_exactDate: string;
  schedule_startTime: string;
  schedule_endTime: string;
  assistanceCheck_id: number;
  assistanceCheck_checkDate: string;
  assistanceCheck_checkTime: string;
  assistanceCheckout_id: number;
  assistanceCheckout_checkDate: string;
  assistanceCheckout_checkTime: string;
  branch_id: number;
  branch_name: string;
  partner_id: number;
  person_firstName: string;
  person_lastName: string;
  person_secondLastName: string;
  candidate_monthlySalary: string;
  position_name: string;
  periodicity_name: string;
  freeDays: boolean;
  incentives: Incentive[];
  deductions: Deduction[];
  totalIncidents: number;
  isHolliday: boolean;
  isCelebrityDayWorked?: boolean;
  celebrityDay?: CelebrityDay | null;
  incidents: Incident[];
}

export interface IPayrollReport {
  id: string;
  name: string;
  salary: string;
  dailySalary: string;
  position: string;
  branch: string;
  daysWorked: number | number;
  jornadasCompletas?: number;
  totalHoursWorked: string | number;
  doubleShifts: string | number;
  extraHours: string | number;
  schedules: IPayroll[];
  payment: string;
  payPerHour: string;
  partnerId: number;
  periodicity_name: string;
  salaryPerFortnight: string;
  freeDaysWorked: number;
  freeDaysEnjoyed: number;
  totalIncentives: string;
  totalDeductions: string;
  tarjetasDeductions?: string | number;
  totalDeductionsExcludingTarjetas?: string | number;
  totalIncidents: number;
  schedulesIds: number[];
  totalHollydays: number;
  totalPayWithIncentives: string;
  realSchedule: IAuthorizedHours[];
  authorizedHours: number | string;
  index: number;
  paymentDetails: PaymentDetail;
  workedRounded: number;
  calculatedBonuses?: any[];
  calculatedMissingBoxes?: any[];
  calculatedNewDeductions?: any[];
  celebrityDaysWorked?: number;
  celebrityDaysPayTotal?: string;
  uniformDeduction?: string;
  NormalCalculationNomina?: boolean;
  /** Backend: ajuste de rango por nuevo ingreso en el mes del período */
  showNewHireChip?: boolean;
  /** Fecha de ingreso usada en nómina (debug / validación) */
  payrollRecruitmentDate?: string | null;
  /** Rango operativo del periodo consultado (debug) */
  payrollPeriodRange?: string | null;
  /** Detalle precalculado (snapshot / batch) para evitar peticiones por fila */
  snapshotDetail?: {
    paymentDetail?: unknown;
    chartDaysWorked?: unknown;
  } | null;
}

export const PayrollReport = (
  records: IPayroll[],
  start: string,
  end: string,
  incentives: IncentiveResponse[] = [],
  deductions: DeductionResponse[] = [],
  freeDays: FreeDayResponse[] = [],
  missingBoxes: any[] = [],
  uniformDeductions: { partnerId: number; amountPerPeriod: number }[] = [],
  newDeductions: any[] = []
): IPayrollReport[] => {
  const partners = [...new Set(records.map((partner) => partner.partner_id))];
  let i = 0;
  const schedule: IPayrollReport[] = [];
  for (const partner of partners) {
    const incentivePerPartner = incentives
      .filter((incentive) => incentive.partnerId === partner)
      .map((incentive) => incentive.incentives)
      .flat();

    const deductionsPerPartner = deductions
      .filter((deduction) => deduction.partnerId === partner)
      .map((deduction) => deduction.deductions)
      .flat();

    const freeDaysPerPartner = freeDays
      .filter((freeDay) => freeDay.partnerId === partner)
      .map((freeDay) => freeDay.freeDays)
      .flat()
      .map((freeDay) => getExactDateFromJSON(freeDay))
      .filter((date) => date !== null);

    const missingBoxesPerPartner = missingBoxes
      .filter((missingBox) => missingBox.partnerId === partner)
      .map((missingBox) => missingBox.missingBoxes)
      .flat();

    const newDeductionsPerPartner = newDeductions
      .filter((deduction) => deduction.partner?.id === partner)
      .map((deduction) => ({
        name: deduction.name,
        amount: deduction.amount,
        frequency: deduction.frequency,
        startDate: deduction.startDate,
        endDate: deduction.endDate,
        applicationDate: deduction.applicationDate,
      }));

    const data = records.filter((record) => record.partner_id === partner);
    const schedules = Array.from(
      new Map(
        data.map((record) => [record.schedule_exactDate, record])
      ).values()
    );

    const { freeDaysEnjoyed, freeDaysWorked } = freeDaysTest(
      schedules,
      freeDaysPerPartner
    );

    const person = schedules.at(0);
    const NormalCalculationNominaValue = (person as any)?.NormalCalculationNomina || (person as any)?.partner_NormalCalculationNomina || false;

    const dayToPay = getDaysToPay(person?.periodicity_name!);

    const dailySalary = Number(person?.candidate_monthlySalary) / dayToPay;
    // Por ahora mantenemos 8.5 como default temporal
    const fullDayHours = 8.5; 
    const hourlyPay = dailySalary / fullDayHours;
    const salaryPerFortnight = dailySalary * 15;

    const payFreeDaysWorked =
      freeDaysWorked * withFreeDayWorkedPremium(dailySalary);

    const totalDeductions = deductionsPerPartner.reduce((acc, deduction) => {
      const { amount, frequency, applicationDate, startDate, endDate } = deduction as any;
      const days = getDaysBetweenDates(start, end);

      if (frequency === "único" && applicationDate && days.has(applicationDate)) {
        return acc + Number(amount);
      }

      const bonusStartDate = startDate || start;
      const bonusEndDate = endDate || end;
      if (bonusEndDate < start || bonusStartDate > end) {
        return acc;
      }
      const effectiveStart = bonusStartDate > start ? bonusStartDate : start;
      const effectiveEnd = bonusEndDate < end ? bonusEndDate : end;
      const daysInEffectiveRange = getDaysBetweenDates(
        effectiveStart,
        effectiveEnd
      ).size;
      const daysPerPeriod = getDayPerIncentive(frequency);
      const periodsInRange = Math.floor(daysInEffectiveRange / daysPerPeriod);
      return acc + Number(amount) * periodsInRange;
    }, 0);
    const totalMissingBoxDeductions = missingBoxesPerPartner.reduce(
      (acc, missingBox) => {
        const { amountPerPeriod, startPeriodDate, endPeriodDate } = missingBox;
        const startPeriod = new Date(startPeriodDate * 1000)
          .toISOString()
          .split("T")[0];
        const endPeriod = new Date(endPeriodDate * 1000)
          .toISOString()
          .split("T")[0];
        if (endPeriod < start || startPeriod > end) {
          return acc;
        }
        const effectiveStart = startPeriod > start ? startPeriod : start;
        const effectiveEnd = endPeriod < end ? endPeriod : end;
        const daysInEffectiveRange = getDaysBetweenDates(
          effectiveStart,
          effectiveEnd
        ).size;
        const daysPerPeriod = 15;
        const periodsInRange = Math.floor(daysInEffectiveRange / daysPerPeriod);

        const totalDeduction = Number(amountPerPeriod) * periodsInRange;

        return acc + totalDeduction;
      },
      0
    );

    const totalNewDeductions = newDeductionsPerPartner.reduce((acc, deduction) => {
      const { amount, frequency, applicationDate, startDate, endDate } = deduction;
      const days = getDaysBetweenDates(start, end);
      
      if (frequency === "único") {
        if (applicationDate && days.has(applicationDate)) {
          return acc + Number(amount);
        }
        return acc;
      }

      const bonusStartDate = startDate || start;
      const bonusEndDate = endDate || end;

      if (!(bonusEndDate < start || bonusStartDate > end)) {
        const effectiveStart = bonusStartDate > start ? bonusStartDate : start;
        const effectiveEnd = bonusEndDate < end ? bonusEndDate : end;
        const daysInEffectiveRange = getDaysBetweenDates(
          effectiveStart,
          effectiveEnd
        ).size;
        const daysPerPeriod = getDayPerIncentive(frequency);
        const periodsInRange = Math.floor(
          daysInEffectiveRange / daysPerPeriod
        );
        return acc + Number(amount) * periodsInRange;
      }
      
      return acc;
    }, 0);

    const totalIncentives = incentivePerPartner.reduce((acc, incentive) => {
      const { amount, frequency, applicationDate, startDate, endDate } =
        incentive;

      if (frequency === "único") {
        if (
          applicationDate &&
          applicationDate >= start &&
          applicationDate <= end
        ) {
          return acc + Number(amount);
        }

        return acc;
      }

      const bonusStartDate = startDate || start;
      const bonusEndDate = endDate || end;

      if (bonusEndDate < start || bonusStartDate > end) {
        return acc;
      }

      const effectiveStart = bonusStartDate > start ? bonusStartDate : start;
      const effectiveEnd = bonusEndDate < end ? bonusEndDate : end;

      const daysInEffectiveRange = getDaysBetweenDates(
        effectiveStart,
        effectiveEnd
      ).size;

      const daysPerPeriod = getDayPerIncentive(frequency);
      const periodsInRange = Math.floor(daysInEffectiveRange / daysPerPeriod);

      const totalPay = Number(amount) * periodsInRange;
      return acc + totalPay;
    }, 0);

    const calculatedBonuses = incentivePerPartner
      .map((incentive) => {
        const { amount, frequency, applicationDate, startDate, endDate } =
          incentive;
        let calculatedAmount = 0;

        if (frequency === "único") {
          if (
            applicationDate &&
            applicationDate >= start &&
            applicationDate <= end
          ) {
            calculatedAmount = Number(amount);
          }
        } else {
          const bonusStartDate = startDate || start;
          const bonusEndDate = endDate || end;

          if (!(bonusEndDate < start || bonusStartDate > end)) {
            const effectiveStart =
              bonusStartDate > start ? bonusStartDate : start;
            const effectiveEnd = bonusEndDate < end ? bonusEndDate : end;
            const daysInEffectiveRange = getDaysBetweenDates(
              effectiveStart,
              effectiveEnd
            ).size;
            const daysPerPeriod = getDayPerIncentive(frequency);
            const periodsInRange = Math.floor(
              daysInEffectiveRange / daysPerPeriod
            );
            calculatedAmount = Number(amount) * periodsInRange;
          }
        }

        return {
          name: incentive.name,
          amount: incentive.amount,
          frequency: incentive.frequency,
          startDate: incentive.startDate,
          endDate: incentive.endDate,
          calculatedAmount,
        };
      })
      .filter((bonus) => bonus.calculatedAmount > 0);

    const calculatedMissingBoxes = missingBoxesPerPartner
      .map((missingBox) => {
        const { amountPerPeriod, startPeriodDate, endPeriodDate, note } =
          missingBox;

        const startPeriod = new Date(startPeriodDate * 1000)
          .toISOString()
          .split("T")[0];
        const endPeriod = new Date(endPeriodDate * 1000)
          .toISOString()
          .split("T")[0];

        if (endPeriod < start || startPeriod > end) {
          return null;
        }

        const effectiveStart = startPeriod > start ? startPeriod : start;
        const effectiveEnd = endPeriod < end ? endPeriod : end;

        const daysInEffectiveRange = getDaysBetweenDates(
          effectiveStart,
          effectiveEnd
        ).size;

        const daysPerPeriod = 15;
        const periodsInRange = Math.floor(daysInEffectiveRange / daysPerPeriod);

        const calculatedAmount = Number(amountPerPeriod) * periodsInRange;

        return {
          name: `Faltante en caja - ${note || "Sin descripción"}`,
          amount: amountPerPeriod,
          frequency: "quincenal",
          startDate: startPeriod,
          endDate: endPeriod,
          calculatedAmount,
        };
      })
      .filter(
        (missingBox) => missingBox !== null && missingBox.calculatedAmount > 0
      );

    const calculatedNewDeductions = newDeductionsPerPartner
      .map((deduction) => {
        const { amount, frequency, applicationDate, startDate, endDate } = deduction;
        let calculatedAmount = 0;

        if (frequency === "único") {
          if (applicationDate && getDaysBetweenDates(start, end).has(applicationDate)) {
            calculatedAmount = Number(amount);
          }
        } else {
          const bonusStartDate = startDate || start;
          const bonusEndDate = endDate || end;

          if (!(bonusEndDate < start || bonusStartDate > end)) {
            const effectiveStart = bonusStartDate > start ? bonusStartDate : start;
            const effectiveEnd = bonusEndDate < end ? bonusEndDate : end;
            const daysInEffectiveRange = getDaysBetweenDates(
              effectiveStart,
              effectiveEnd
            ).size;
            const daysPerPeriod = getDayPerIncentive(frequency);
            const periodsInRange = Math.floor(
              daysInEffectiveRange / daysPerPeriod
            );
            calculatedAmount = Number(amount) * periodsInRange;
          }
        }

        return {
          name: deduction.name,
          amount: deduction.amount,
          frequency: deduction.frequency,
          startDate: deduction.startDate,
          endDate: deduction.endDate,
          calculatedAmount,
        };
      })
      .filter((deduction) => deduction.calculatedAmount > 0);

    const totalIncidents = schedules.reduce((acc, record) => {
      const { totalIncidents } = record;
      return acc + totalIncidents;
    }, 0);

    const totalHollydays = schedules.reduce((acc, record) => {
      const { isHolliday } = record;
      return acc + (isHolliday ? 1 : 0);
    }, 0);

    const celebrityDaysSet = new Set();
    schedules.forEach((record) => {
      if (record.isCelebrityDayWorked && record.celebrityDay) {
        const dateKey = new Date(record.schedule_exactDate)
          .toISOString()
          .split("T")[0];
        celebrityDaysSet.add(dateKey);
      }
    });
    const celebrityDaysWorked = celebrityDaysSet.size;

    const celebrityDaysPayMap = new Map();
    schedules.forEach((record) => {
      if (record.isCelebrityDayWorked && record.celebrityDay) {
        const dateKey = new Date(record.schedule_exactDate)
          .toISOString()
          .split("T")[0];

        if (!celebrityDaysPayMap.has(dateKey)) {
          const multiplier = parseCelebrityDayMultiplier(
            record.celebrityDay.bonusType
          );
          celebrityDaysPayMap.set(dateKey, dailySalary * multiplier);
        }
      }
    });
    const celebrityDaysPayTotal = Array.from(
      celebrityDaysPayMap.values()
    ).reduce((sum, pay) => sum + pay, 0);

    const authorized = scheduleAfterIncidents(schedules);

    const payment = calculatePayment(
      authorized,
      Math.round(hourlyPay * 100) / 100,
      freeDaysWorked,
      freeDaysEnjoyed,
      schedules,
      freeDaysPerPartner
    );

    const realSchedule = authorized.realSchedule;

    const uniformDeductionAmount =
      uniformDeductions.find((u) => u.partnerId === person?.partner_id!)?.amountPerPeriod || 0;

    schedule.push({
      id: uuid(),
      index: i,
      name: `${toCamelCase(
        `${person?.person_firstName} ${person?.person_lastName} ${person?.person_secondLastName}`
      )}`,
      salary: getCurrencyFormat(person?.candidate_monthlySalary!),
      dailySalary: getCurrencyFormat(dailySalary),
      position: person?.position_name!,
      branch: person?.branch_name!,
      daysWorked: authorized.dayWorked,

      workedRounded: authorized.workedRounded,
      totalHoursWorked: authorized.hoursWorked.toFixed(1),
      doubleShifts: authorized.doubleShifts.toFixed(1),
      extraHours: authorized.extraHours.toFixed(1),

      authorizedHours: authorized.hoursAuthorized.toFixed(1),

      schedules,

      payPerHour: getCurrencyFormat(hourlyPay),
      partnerId: person?.partner_id!,
      periodicity_name: person?.periodicity_name!,
      salaryPerFortnight: getCurrencyFormat(salaryPerFortnight),

      payment: payment.totalPay,
      freeDaysWorked,
      freeDaysEnjoyed,
      totalIncidents,
      totalHollydays,

      totalIncentives: getCurrencyFormat(totalIncentives),
      celebrityDaysWorked,
      celebrityDaysPayTotal: payment.celebrityDaysPayTotal,

      // Importante: no sumar "newDeductions" al total para evitar doble conteo si ya existen en deductions
      totalPayWithIncentives: getCurrencyFormat(
        payment.totalPayNumber +
          totalIncentives -
          totalDeductions -
          totalMissingBoxDeductions -
          uniformDeductionAmount
      ),
      totalDeductions: getCurrencyFormat(
        totalDeductions + totalMissingBoxDeductions + uniformDeductionAmount
      ),
      uniformDeduction: getCurrencyFormat(uniformDeductionAmount),

      schedulesIds: schedules.map((record) => record.schedule_id),
      realSchedule,
      paymentDetails: payment,
      calculatedBonuses,
      calculatedMissingBoxes,
      calculatedNewDeductions,
      celebrityDaysWorked,
      celebrityDaysPayTotal: getCurrencyFormat(celebrityDaysPayTotal),
      NormalCalculationNomina: NormalCalculationNominaValue,
    });
    i++;
  }
  return schedule;
};

export const PayrollUpdate = (
  record: IPayrollReport,
  incidentId: string,
  finalStatus: boolean
): IPayrollReport => {
  const { schedules } = record;

  const updatedSchedules = schedules.map((record) => ({
    ...record,
    incidents: record.incidents.map((data) =>
      data.id === incidentId 
        ? { 
            ...data, 
            finalStatus,
            updateAt: new Date().toISOString()
          } 
        : data
    ),
  }));

  const authorized = scheduleAfterIncidents(updatedSchedules);

  const realSchedule = authorized.realSchedule;

  return {
    ...record,
    daysWorked: authorized.dayWorked,

    workedRounded: authorized.workedRounded,
    totalHoursWorked: authorized.hoursWorked.toFixed(1),
    doubleShifts: authorized.doubleShifts.toFixed(1),
    extraHours: authorized.extraHours.toFixed(1),
    authorizedHours: Math.floor(authorized.hoursAuthorized),
    realSchedule,
    schedules: updatedSchedules,
  };
};
